import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {Button, message, Tag, Tabs, Select, Modal} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {hasResource} from "../../../utils/resourceAuth";
import {ResourceCode} from "../../../constants/resourceCodes";
import {useHashState} from 'utils/hashState';
import {PickupAppointmentInitialValue} from "./graphql/queries/getPickupAppointmentListing";
import {PickupAppointmentType} from "./graphql/queries/getPickupAppointmentListing";
import {usePickupAppointmentQuery} from "./hooks/usePickupAppointmentQuery";
import PickupAppointmentFilter from "./components/PickupAppointmentFilter";
import PickupAppointmentColumns from "./components/PickupAppointmentColumns";
import PickupAppointmentDrawer from "./components/PickupAppointmentDrawer";
import PickupTransferTaskModal from "./components/PickupTransferTaskModal";
import PickupTaskCreateDrawer from "./components/PickupTaskCreateDrawer";
import PickupTaskDetailDrawer from "../pickup-task/components/PickupTaskDetailDrawer";
import {PickupDataProvider} from "../pickup-task/contexts/PickupDataContext";
import PickupMergeAppointmentModal from "./components/PickupMergeAppointmentModal";
import PickupAppointmentCopyModal from "./components/PickupAppointmentCopyModal";
import AppointmentDownloadModal from "./components/AppointmentDownloadModal";
import {useMutation, useLazyQuery} from "@apollo/client";
import {TRANSFER_PICKUP_TASK_APPOINTMENT} from "../pickup-task/graphql/mutations/transferPickupTaskAppointment";
import {VALIDATE_PICKUP_APPOINTMENTS_COOPERATION_WAREHOUSE_STATUS} from "./graphql/queries/validatePickupAppointmentsCooperationWarehouseStatus";
import { CONFIRM_PICKUP_APPOINTMENTS, ConfirmPickupAppointmentsResponse } from "./graphql/mutations/confirmPickupAppointments";
import { CHECK_EXIST_BY_RELATED_NO, CheckExistByRelatedNoResponse } from "./graphql/queries/checkExistByRelatedNo";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getCurrentTimeInTimezone, isBeforeTime } from '../../../utils/timezoneUtils';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import {packageUnitFilterOptions} from "./types/PickupAppointmentType";
import { useTranslation } from 'react-i18next';
import {PickupSourceEnum} from "../../../enums/PickupSourceEnum";
import TablePaginationBar from '../../../components/TablePaginationBar';
import {
    buildPickupAppointmentDownloadFilters,
    useGeneratePickupAppointmentReport
} from "./hooks/useGeneratePickupAppointmentReport";

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

const PickupAppointmentsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [detailVisible, setDetailVisible] = useState(false);
    const [appointmentId, setAppointmentId] = useState<number>();
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'detail'>('detail');
    // 复揽跳转：创建模式下预填的揽收站点 ID。
    // 同步从 URL 捕获(在 useHashState 等可能重写 URL 之前)，避免 effect 里读不到 pickupSiteId。
    const [createInitialSiteId, setCreateInitialSiteId] = useState<number | undefined>(() => {
        const sid = new URLSearchParams(window.location.search).get('pickupSiteId');
        const n = sid ? Number(sid) : NaN;
        return Number.isNaN(n) ? undefined : n;
    });
    // 复揽跳转：创建模式下预填的渠道 ID（同源同步从 URL 捕获）。
    const [createInitialChannelId, setCreateInitialChannelId] = useState<number | undefined>(() => {
        const cid = new URLSearchParams(window.location.search).get('channelId');
        const n = cid ? Number(cid) : NaN;
        return Number.isNaN(n) ? undefined : n;
    });
    // 复揽跳转：原疑似虚假单 ID（同源同步从 URL 捕获），建单落库供精确判定已完结。
    const [createInitialRepickupSourceId] = useState<number | undefined>(() => {
        const sid = new URLSearchParams(window.location.search).get('sourceAppointmentId');
        const n = sid ? Number(sid) : NaN;
        return Number.isNaN(n) ? undefined : n;
    });
    // 复揽跳转：预填的手动加单原因（同源同步从 URL 捕获）。
    // 由来源抽屉按「目标日该站点有无未取消单」算好——后端 validateUniqueness 对特殊原因与
    // 常规上门的放行条件严格互补，固定取值会在某一侧必然被拒。
    const [createInitialManualReason] = useState<string | undefined>(() => {
        const reason = new URLSearchParams(window.location.search).get('manualReason');
        return reason || undefined;
    });

    const { currentWarehouse } = useWarehouseContext();
    const warehouse = currentWarehouse;
    const warehouseId = warehouse?.id!;

    // 初始化时检查是否有URL参数，如果有则构建初始状态
    const getInitialState = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const routeId = urlParams.get('routeId');
        const expectedPickupDate = urlParams.get('expectedPickupDate');
        const status = urlParams.get('status');
        const confirm = urlParams.get('confirm');
        
        // 如果有URL参数，构建对应的初始状态
        if (routeId || expectedPickupDate || status || confirm) {
            let initialFilters = { ...PickupAppointmentInitialValue };
            
            if (routeId) {
                initialFilters.routeId = parseInt(routeId);
            }
            
            if (expectedPickupDate) {
                initialFilters.expectedPickupDate = expectedPickupDate;
            }
            
            if (status) {
                initialFilters.status = status;
            }
            
            if (confirm) {
                initialFilters.confirm = parseInt(confirm);
            }
            
            return initialFilters;
        }
        
        // 没有URL参数时，使用默认值
        const today = dayjs().format('YYYY-MM-DD');
        return {
            ...PickupAppointmentInitialValue,
            expectedPickupDate: today,
            confirm: 0
        };
    };
    
    const [tempFilters, setTempFilters] = useHashState(getInitialState());
    const [filters, setFilters] = useHashState(getInitialState());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const {t} = useTranslation();
    // 添加一个持久化的选择状态，用于跨分页保存选择
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);

    // 保存所有选中的预约单数据，用于计算汇总信息
    const [selectedAppointments, setSelectedAppointments] = useState<Map<React.Key, PickupAppointmentType>>(new Map());
    const [sortType, setSortType] = useState<string | null>(null); // 排序类型
    const [filterTrigger, setFilterTrigger] = useState(0);
    const [transferTaskModalVisible, setTransferTaskModalVisible] = useState(false);
    const [taskCreateDrawerVisible, setTaskCreateDrawerVisible] = useState(false);
    const [taskDetailDrawerVisible, setTaskDetailDrawerVisible] = useState(false);
    const [mergeAppointmentModalVisible, setMergeAppointmentModalVisible] = useState(false);
    const [copyAppointmentModalVisible, setCopyAppointmentModalVisible] = useState(false);
    const [appointmentFileName, setAppointmentFileName] = useState('');
    const [appointmentDownloadVisible, setAppointmentDownloadVisible] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState<string>('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const canOperatePickupAppointment = hasResource([ResourceCode.menu.PICKUP_APPOINTMENT_OPERATE]);

    // 用于标记 URL 参数是否已处理
    const urlParamsProcessed = useRef(false);
    const lastCheckExistTriggerRef = useRef<number | null>(null);
    const { submit: generateAppointmentReport } = useGeneratePickupAppointmentReport();

    // 添加一个状态来触发 canCreateAppointment 的重新计算
    const [currentTime, setCurrentTime] = useState(() => dayjs());

    // 刷新页面时清空除 expectedPickupDate 外的所有筛选项
    useEffect(() => {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (nav?.type === 'reload') {
            const today = dayjs().format('YYYY-MM-DD');
            const fresh = { ...PickupAppointmentInitialValue, expectedPickupDate: today, confirm: 0 };
            setFilters(fresh);
            setTempFilters(fresh);
        }
    }, []);

    // 每分钟更新一次当前时间，确保 canCreateAppointment 能够及时更新
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(dayjs());
        }, 60000); // 每分钟更新一次

        return () => clearInterval(timer);
    }, []);

    // 检查是否可以创建预约（基于列表的 Pickup Date）
    const canCreateAppointment = useMemo(() => {
        // 如果没有warehouse信息或时区信息，默认允许创建
        if (!warehouse || !warehouse.timezone) {
            return true;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);
            
            // 获取列表筛选的 Pickup Date
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
            
            // 如果列表的 Pickup Date 是过去的日期，不能创建预约
            if (dayjs(listPickupDate).isBefore(dayjs(today))) {
                return false;
            }
            
            // 如果列表的 Pickup Date 是未来日期，可以创建预约
            if (dayjs(listPickupDate).isAfter(dayjs(today))) {
                return true;
            }
            
            // 如果列表的 Pickup Date 是今天，检查截止时间限制
            // 如果没有设置appointmentDeadlineTime，则整天都可以创建预约
            if (!warehouse.appointmentDeadlineTime) {
                return true;
            }
            
            // 如果设置了appointmentDeadlineTime，检查是否超过截止时间
            const [hours, minutes] = warehouse.appointmentDeadlineTime.split(':').map(Number);
            const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                .hour(hours)
                .minute(minutes)
                .second(0)
                .millisecond(0);

            return currentTimeInWarehouseZone.isBefore(todayDeadlineInWarehouseZone);
        } catch (error) {
            console.error('Error checking appointment deadline:', error);
            return true;
        }
    }, [warehouse, filters.expectedPickupDate, currentTime, refreshTrigger]);


    // 检查选择的日期是否是今天以前的日期
    const isPastDate = useMemo(() => {
        // 如果没有warehouse信息或时区信息，默认不认为是过去日期
        if (!warehouse || !warehouse.timezone) {
            return false;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);

            // 获取列表筛选的 Pickup Date
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

            // 如果列表的 Pickup Date 是过去的日期，返回 true
            return dayjs(listPickupDate).isBefore(dayjs(today));
        } catch (error) {
            console.error('Error checking if date is past:', error);
            return false;
        }
    }, [warehouse, filters.expectedPickupDate, currentTime]);

    // 复揽跳转：createInitialSiteId 已在初始化时同步从 URL 捕获，这里只负责自动打开创建抽屉。
    const reschedulePrefillProcessed = useRef(false);
    useEffect(() => {
        if (reschedulePrefillProcessed.current) return;
        reschedulePrefillProcessed.current = true;
        // 带 openCreate state 进来的（复揽「安排司机上门」）交给下面那个 effect 独占：
        // 它要先把揽收日期设成目标日期再开抽屉。这里抢先用旧日期打开，会让抽屉开着的时候
        // minimumPickupDate 再跳变一次，触发 create 初始化 effect 的 resetFields 把站点冲掉
        if ((location.state as { openCreate?: boolean } | null)?.openCreate) return;
        if (createInitialSiteId != null) {
            setAppointmentId(undefined);
            setDrawerMode('create');
            setDetailVisible(true);
        }
    }, [createInitialSiteId]);

    // 处理URL参数到hash的转换（仅在需要时执行）
    useEffect(() => {
        if (urlParamsProcessed.current) return;

        const urlParams = new URLSearchParams(window.location.search);
        const hasUrlParams = urlParams.get('routeId') || urlParams.get('expectedPickupDate') ||
                           urlParams.get('status') || urlParams.get('confirm');
        
        // 只有在有URL参数时才需要转换
        if (hasUrlParams) {
            // 构建 hash 值（使用当前已设置的状态）
            const hashValue = Object.entries(filters)
                .filter(([key, value]) => value !== undefined && value !== null)
                .map(([key, value]) => {
                    const encodedKey = encodeURIComponent(key);
                    const encodedValue = encodeURIComponent(
                        Array.isArray(value) ? JSON.stringify(value) : String(value),
                    );
                    return `${encodedKey}=${encodedValue}`;
                })
                .join('&');
            
            // 使用 replaceState 一次性更新 URL，避免页面刷新
            const newUrl = new URL(window.location.href);
            newUrl.search = '';
            newUrl.hash = hashValue;
            window.history.replaceState({}, '', newUrl.toString());
        }
        
        urlParamsProcessed.current = true;
    }, [filters]); // 依赖 filters，确保状态已正确设置

    // 当列表的 Pickup Date 超过截止时间时显示警告提示（仅显示一次）
    useEffect(() => {
        if (!canCreateAppointment && warehouse?.timezone) {
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
            
            // 只在页面首次加载时显示提示
            const hasShownToday = sessionStorage.getItem(`appointment-deadline-warning-${today}-${listPickupDate}`);
            if (!hasShownToday) {
                let warningMessage = '';
                if (dayjs(listPickupDate).isBefore(dayjs(today))) {
                    warningMessage = `Appointments for ${listPickupDate} cannot be created: this date is in the past. Please select today or a future date.`;
                } else if (dayjs(listPickupDate).isSame(dayjs(today))) {
                    // 只有在设置了appointmentDeadlineTime且超过截止时间时才显示提示
                    if (warehouse?.appointmentDeadlineTime) {
                        warningMessage = `Current time (${currentTimeInWarehouseZone.format('HH:mm')}) has passed the appointment deadline (${warehouse.appointmentDeadlineTime}) in ${warehouse.timezone}. Appointments for ${listPickupDate} cannot be created. Please select a future date.`;
                    }
                }
                
                if (warningMessage) {
                    message.warning(warningMessage);
                    sessionStorage.setItem(`appointment-deadline-warning-${today}-${listPickupDate}`, 'shown');
                }
            }
        }
    }, [canCreateAppointment, warehouse, filters.expectedPickupDate]);

    // Transfer mutation
    const [transferAppointmentsToTask, {loading: transferLoading}] = useMutation(TRANSFER_PICKUP_TASK_APPOINTMENT);
    const [confirmAppointments, { loading: confirmLoading }] = useMutation<ConfirmPickupAppointmentsResponse>(CONFIRM_PICKUP_APPOINTMENTS);

    // Validation query for cooperation warehouse status
    const [validateCooperationWarehouseStatus, {loading: validationLoading}] = useLazyQuery(VALIDATE_PICKUP_APPOINTMENTS_COOPERATION_WAREHOUSE_STATUS);
    const [checkExistByRelatedNo] = useLazyQuery<CheckExistByRelatedNoResponse>(CHECK_EXIST_BY_RELATED_NO);

    // 使用自定义hook获取数据，传递当前真实的 warehouseId
    const {
        appointmentData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables,
        pendingCount,
        confirmedCount,
        countsLoading,
        refetchCounts
    } = usePickupAppointmentQuery(filters, currentPage, pageSize, warehouseId);
    // 应用排序的数据
    const sortedAppointmentData = useMemo(() => {
        if (!sortType || !appointmentData) return appointmentData;

        const sorted = [...appointmentData];
        // 地址排序键：忽略前缀数字/符号，从首个字母开始（不区分大小写）
        const getAddressSortKey = (address?: string): string => {
            const s = (address || '').trim();
            const match = s.match(/[A-Za-z]/);
            if (!match) return s.toLowerCase();
            const idx = match.index || 0;
            return s.substring(idx).toLowerCase();
        };

        switch (sortType) {
            case 'Updatetime-asc':
                return sorted.sort((a, b) =>
                    (b.updatedAt).localeCompare(a.updatedAt)
                );
            case 'Updatetime-desc':
                return sorted.sort((a, b) =>
                    (a.updatedAt).localeCompare(b.updatedAt)
                );
            case 'address-asc':
                return sorted.sort((a, b) => {
                    const ak = getAddressSortKey(a.addressLine1);
                    const bk = getAddressSortKey(b.addressLine1);
                    return ak.localeCompare(bk);
                });
            case 'address-desc':
                return sorted.sort((a, b) => {
                    const ak = getAddressSortKey(a.addressLine1);
                    const bk = getAddressSortKey(b.addressLine1);
                    return bk.localeCompare(ak);
                });
            case 'zipcode-asc':
                return sorted.sort((a, b) =>
                    (a.zipcode || '').localeCompare(b.zipcode || '')
                );
            case 'zipcode-desc':
                return sorted.sort((a, b) =>
                    (b.zipcode || '').localeCompare(a.zipcode || '')
                );
            default:
                return sorted;
        }
    }, [appointmentData, sortType]);
    // 更新处理选择变化的函数，支持跨分页选择
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => {
        // 更新当前页面的选择
        setSelectedRowKeys(newSelectedRowKeys);

        // 更新持久化选择
        const newPersistentKeys = [...persistentSelection];
        const newSelectedAppointments = new Map(selectedAppointments);

        // 处理当前页面上的选择变化 - 使用sortedAppointmentData而不是appointmentData
        const currentPageData = sortedAppointmentData || appointmentData;
        currentPageData.forEach(appointment => {
            const id = appointment.id;
            const isSelected = newSelectedRowKeys.includes(id);

            // 如果选中了，但不在持久化选择中，则添加
            if (isSelected && !newPersistentKeys.includes(id)) {
                newPersistentKeys.push(id);
                newSelectedAppointments.set(id, appointment);
            }

            // 如果取消选中，但在持久化选择中，则移除
            if (!isSelected && newPersistentKeys.includes(id)) {
                const index = newPersistentKeys.indexOf(id);
                if (index > -1) {
                    newPersistentKeys.splice(index, 1);
                    newSelectedAppointments.delete(id);
                }
            }
        });

        // 更新持久化选择状态
        setPersistentSelection(newPersistentKeys);
        setSelectedAppointments(newSelectedAppointments);
    };

    // 当页面数据加载完成后，根据持久化选择更新当前页面的选择状态
    useEffect(() => {
        if (!loading && appointmentData.length > 0) {
            // 使用sortedAppointmentData而不是appointmentData来保持选择状态与排序后的数据一致
            const currentPageData = sortedAppointmentData || appointmentData;

            // 找出当前页面中应该被选中的行
            const currentPageKeys = currentPageData
                .filter(appointment => persistentSelection.includes(appointment.id))
                .map(appointment => appointment.id);

            setSelectedRowKeys(currentPageKeys);

            // 更新选中的预约单数据
            const newSelectedAppointments = new Map(selectedAppointments);
            currentPageData.forEach(appointment => {
                if (persistentSelection.includes(appointment.id)) {
                    newSelectedAppointments.set(appointment.id, appointment);
                }
            });
            setSelectedAppointments(newSelectedAppointments);
        }
    }, [appointmentData, sortedAppointmentData, loading]);

    useEffect(() => {
        if (filterTrigger === 0) {
            return;
        }

        if (lastCheckExistTriggerRef.current === filterTrigger) {
            return;
        }

        if (loading || countsLoading) {
            return;
        }
        if (pendingCount != 0 || confirmedCount != 0) {
            return;
        }

        const trackingNumber = filters.trackingNumber?.toString().trim();
        const pickupSerialNo = filters.pickupSerialNo?.toString().trim();

        if (!trackingNumber && !pickupSerialNo) {
            return;
        }

        const variables: {
            trackingNumber?: string;
            pickupSerialNo?: string;
        } = {};

        if (trackingNumber) {
            variables.trackingNumber = trackingNumber;
        }

        if (pickupSerialNo) {
            variables.pickupSerialNo = pickupSerialNo;
        }

        lastCheckExistTriggerRef.current = filterTrigger;

        checkExistByRelatedNo({
            variables,
            fetchPolicy: 'network-only'
        })
            .then(result => {
                const relatedMessage = result.data?.checkExistByRelatedNo?.trim();
                if(relatedMessage == null || relatedMessage.length == 0){
                    return
                }
                if (relatedMessage != filters.expectedPickupDate ) {
                    Modal.info({
                        title: t('pickup.appointment.messages.relatedNoExistsTitle'),
                        content: t('pickup.appointment.messages.relatedNoExistsContent', {
                            date: relatedMessage
                        })
                    });
                }
            })
            .catch(error => {
                console.error('Failed to check related number:', error);
            });
    }, [
        checkExistByRelatedNo,
        filterTrigger,
        loading,
        countsLoading,
        pendingCount,
        confirmedCount,
        filters.trackingNumber,
        filters.pickupSerialNo
    ]);

    // 计算选中项的汇总信息
    const selectedSummary = useMemo(() => {
        if (selectedAppointments.size === 0) {
            return '';
        }

        // 按单位类型统计数量
        const unitCounts = new Map<string, number>();

        // 遍历所有选中的预约单，统计不同单位的数量
        selectedAppointments.forEach((appointment) => {
            const count = appointment.expectedParcelCount || 0;
            const unit = appointment.expectedParcelUnit || 'PIECE';

            // 按单位类型累加
            const currentCount = unitCounts.get(unit) || 0;
            unitCounts.set(unit, currentCount + count);
        });

        // 构建显示字符串，格式为: "2 PIECES + 3 BAG + 4 BOX"
        const unitParts: string[] = [];

        // 按照packageUnitFilterOptions的顺序显示单位
        packageUnitFilterOptions.forEach(option => {
            const count = unitCounts.get(option.value);
            if (count && count > 0) {
                unitParts.push(`${count} ${option.label}`);
            }
        });

        // 如果没有任何单位有数量，返回空字符串
        if (unitParts.length === 0) {
            return '';
        }

        // 用 "+" 连接不同单位的数量
        return unitParts.join(' + ');
    }, [selectedAppointments]);

    const handleResetFilters = useCallback(() => {
        const resetFilters = {
            ...PickupAppointmentInitialValue,
            expectedPickupDate: dayjs().format('YYYY-MM-DD'), // 确保重置时也设置为今天
            confirm: 0 // 重置后默认回到待确认预约单
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    }, []);

    const handleFilter = useCallback(() => {
        setFilters(tempFilters);
        setCurrentPage(1);
        setFilterTrigger(prev => prev + 1);
    }, [tempFilters]);

    // 监听日期变化，自动清空选择状态
    useEffect(() => {
        // 当日期变化时，清空选择状态，避免将不同日期的预约单关联
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
    }, [filters.expectedPickupDate]);

    // 监听 refreshTrigger 变化时执行查询（filters 变化由 useQuery 自动处理）
    useEffect(() => {
        if (refreshTrigger > 0 && warehouseId) {
            refetch(buildQueryVariables());
            refetchCounts();
        }
    }, [refreshTrigger]);

    const showAppointmentDetail = (record: PickupAppointmentType) => {
        setAppointmentId(record.id);
        setDrawerMode('detail');
        setDetailVisible(true);
    };

    // 处理分页变化
    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    // 切换 Tab：待确认 / 已确认
    const handleTabSwitch = (key: string) => {
        const confirmValue = key === 'confirmed' ? 1 : 0;
        const newFilters = { ...filters, confirm: confirmValue };
        setFilters(newFilters);
        setTempFilters(newFilters);
        setCurrentPage(1);
        // 清空选择项
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
    };

    const handleConfirmAppointment = () => {
        // 仅允许在 Pending 列表中进行确认
        if (filters.confirm !== 0) {
            message.warning(t('pickup.appointment.messages.onlyAppointmentsInPendingConfirmationCanBeConfirmed'));
            return;
        }

        if (persistentSelection.length === 0) {
            message.warning(t('pickup.appointment.messages.pleaseSelectAtLeastOneAppointmentToConfirm'));
            return;
        }

        const appointmentIds = persistentSelection.map((key: React.Key) => Number(key));

        confirmAppointments({ variables: { appointmentIds } })
            .then(() => {
                message.success(t('pickup.appointment.messages.appointmentsConfirmedSuccessfully'));
                // 刷新列表
                setRefreshTrigger(prev => prev + 1);
                // 刷新数量统计
                refetchCounts();
                // 清空选择
                setSelectedRowKeys([]);
                setPersistentSelection([]);
                setSelectedAppointments(new Map());
            })
            .catch(() => {
                message.error(t('pickup.appointment.messages.failedToConfirmAppointments'));
            });
    };

    const handleCreateAppointment = () => {
        // 检查是否超过截止时间，如果超过则显示错误提示并禁止打开抽屉
        if (!canCreateAppointment) {
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse?.timezone || 'UTC');
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');
            
            let errorMessage = '';
            if (dayjs(listPickupDate).isBefore(dayjs(today))) {
                errorMessage = t('pickup.appointment.messages.cannotCreateAppointmentForDate', { date: listPickupDate });
            } else if (dayjs(listPickupDate).isSame(dayjs(today))) {
                if (warehouse?.appointmentDeadlineTime) {
                    errorMessage = t('pickup.appointment.messages.cannotCreateAppointmentForDateDeadline', { date: listPickupDate, currentTime: currentTimeInWarehouseZone.format('HH:mm'), deadline: warehouse.appointmentDeadlineTime, timezone: warehouse.timezone });
                } else {
                    errorMessage = t('pickup.appointment.messages.cannotCreateAppointmentForDateGeneric', { date: listPickupDate });
                }
            } else {
                errorMessage = t('pickup.appointment.messages.cannotCreateAppointmentForDateGeneric', { date: listPickupDate });
            }
            
            message.error(errorMessage);
            return; // 禁止打开抽屉
        }
        
        // 继续创建预约流程（普通新建不预填站点）
        setCreateInitialSiteId(undefined);
        setAppointmentId(undefined);
        setDrawerMode('create');
        setDetailVisible(true);
    };

    // 「安排司机上门」跳转进入：读取路由 state 自动打开建单抽屉并预填站点；跳过列表页 cutoff 预检（抽屉提交时仍会校验）。
    // 揽收日期强制重置为「当前日期」：useHashState 优先读 URL hash，不重置会沿用上次残留的历史日期，
    // 使抽屉的「确定」被 canCreateAppointment 门控锁死（打得开、填得完、提不了）。
    // 口径取 hub 时区而非浏览器本地时区——canCreateAppointment 的 today 用的就是 hub 时区，
    // 两者不一致时（如中国白天 = EWR 前一天晚上）会把日期设成 hub 的"明天"，变成给未来建单。
    useEffect(() => {
        const st = location.state as {
            openCreate?: boolean;
            cooperationWarehouseId?: number;
            /** 复揽「安排司机上门」选「次日」时的目标日期；不传则沿用原行为（当天） */
            expectedPickupDate?: string;
        } | null;
        if (!st?.openCreate) return;
        // 等 warehouse（含 timezone）就绪再算当天，否则会退化成浏览器本地时区
        if (!warehouse) return;
        const todayInHubZone = warehouse.timezone
            ? getCurrentTimeInTimezone(warehouse.timezone).format('YYYY-MM-DD')
            : dayjs().format('YYYY-MM-DD');
        // 调用方指定了目标日期就用它：复揽选「次日」时必须落在次日，回落「今天」会让
        // validateUniqueness 因当日已有原单而判 duplicate，建单直接失败
        const targetDate = st.expectedPickupDate || todayInHubZone;
        setTempFilters((prev: typeof tempFilters) => ({ ...prev, expectedPickupDate: targetDate }));
        setFilters((prev: typeof filters) => ({ ...prev, expectedPickupDate: targetDate }));
        setCurrentPage(1);
        // 只有 state 明确带了站点才覆盖：URL 里的 pickupSiteId 在组件初始化时已同步捕获，
        // 用一个空 state 覆盖会把它冲成 undefined，抽屉就成了空白新建单
        if (st.cooperationWarehouseId != null) {
            setCreateInitialSiteId(st.cooperationWarehouseId);
        }
        setAppointmentId(undefined);
        setDrawerMode('create');
        setDetailVisible(true);
        // 消费后清掉路由 state，避免返回/刷新重复触发
        navigate(location.pathname + location.search, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, warehouse]);

    // 处理任务详情显示
    const handleShowTaskDetail = (taskId: string) => {
        if (!taskId || taskId.trim() === '') {
            message.error(t('pickup.appointment.messages.invalidTaskId'));
            return;
        }
        setCurrentTaskId(taskId);
        setTaskDetailDrawerVisible(true);
    };

    // 更新创建任务的处理函数，使用持久化选择
    const handleCreateTask = async () => {
        if (!filters.expectedPickupDate) {
            message.error(t('pickup.appointment.messages.pleaseSelectPickupDateFirst'));
            return;
        }
        if (persistentSelection.length === 0) {
            message.warning(t('pickup.appointment.messages.pleaseSelectAtLeastOneAppointment'));
            return;
        }

        // 验证选中预约单的合作仓库状态
        const isValid = await validateSelectedAppointments();
        if (!isValid) {
            return; // 验证失败，不打开抽屉
        }

        setTaskCreateDrawerVisible(true);
    };

    // 获取当前选择的 PickupDate，如果没有选择则使用今天的日期
    const getCurrentPickupDate = () => {
        return filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
    };

    // 验证选中预约单的合作仓库状态
    const validateSelectedAppointments = async (): Promise<boolean> => {
        if (persistentSelection.length === 0) {
            return false;
        }

        try {
            const appointmentIds = persistentSelection.map((key: React.Key) => Number(key));
            const result = await validateCooperationWarehouseStatus({
                variables: { ids: appointmentIds }
            });

            const validationResult = result.data?.validatePickupAppointmentsCooperationWarehouseStatus;

            // 检查验证结果
            if (!validationResult?.success) {
                // 验证失败，显示具体的错误信息
                const errorMessage = validationResult?.message || t('pickup.appointment.messages.validationFailed');
                message.error(errorMessage);
                return false;
            }

            return true;
        } catch (error: any) {
            console.error('Transfer error:', error);

            // 提取详细的错误信息
            let errorMessage = t('pickup.appointment.messages.failedToTransferAppointments');

            message.error(errorMessage);
            return false;
        }
    };

    const handleDrawerSuccess = () => {
        refetch(buildQueryVariables());
        // 刷新数量统计
        refetchCounts();
        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
    };

    // 处理任务创建成功
    const handleTaskCreateSuccess = () => {
        refetch(buildQueryVariables());
        // 刷新数量统计
        refetchCounts();

        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());

        // 关闭抽屉
        setTaskCreateDrawerVisible(false);
    };

    // 处理任务详情成功
    const handleTaskDetailSuccess = () => {
        refetch(buildQueryVariables());
        setTaskDetailDrawerVisible(false);
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();
    };
    //打开地图排线窗口
    const handleMapRouting = async () => {
        try {
            // 构建查询参数，传递当前的筛选条件到地图页面
            const searchParams = new URLSearchParams();

            // 传递当前筛选条件
            if (filters.expectedPickupDate) {
                searchParams.set('expectedPickupDate', filters.expectedPickupDate);
            }
            searchParams.set('status', 'CREATED');

            // 导航到地图排线页面，替换当前页面，并传递筛选参数
            const mapUrl = searchParams.toString() ? `/pickup/map?${searchParams.toString()}` : '/pickup/map';
            navigate(mapUrl, { replace: true });
        } catch (error) {
            console.error('导航到地图排线页面失败:', error);
            message.error('打开地图排线页面失败，请重试');
        }
    };
    // 打开转移任务模态框
    const handleAddTask = async () => {
        if (!filters.expectedPickupDate) {
            message.error(t('pickup.appointment.messages.pleaseSelectPickupDateFirst'));
            return;
        }
        if (persistentSelection.length === 0) {
            message.warning(t('pickup.appointment.messages.pleaseSelectAtLeastOneAppointment'));
            return;
        }

        // 验证选中预约单的合作仓库状态
        const isValid = await validateSelectedAppointments();
        if (!isValid) {
            return; // 验证失败，不打开模态框
        }

        // 获取当前任务ID（如果有）
        const taskId = '';
        setCurrentTaskId(taskId);

        // 打开模态框
        setTransferTaskModalVisible(true);
    };

    // 处理任务转移确认
    const handleTransferConfirm = async (targetTaskId: string) => {
        try {
            // 使用持久化选择的ID
            const appointmentIds = persistentSelection.map((key: React.Key) => key.toString());

            await transferAppointmentsToTask({
                variables: {
                    appointmentIds,
                    targetTaskId: targetTaskId,
                    sourceTaskId: null
                }
            });

            // 成功后直接关闭模态框
            setTransferTaskModalVisible(false);

            // 刷新列表
            setRefreshTrigger(prev => prev + 1);
            // 刷新数量统计
            refetchCounts();

            // 清空选择
            setSelectedRowKeys([]);
            setPersistentSelection([]);
            setSelectedAppointments(new Map());

            // 显示成功消息
            message.success(t('pickup.appointment.messages.appointmentsTransferredSuccessfully'));
        } catch (error) {
            message.error(t('pickup.appointment.messages.failedToTransferAppointments'));
            console.error('Transfer error:', error);
        }
    };

    // 处理合并预约
    const handleMergeAppointment = () => {
        if (!filters.expectedPickupDate) {
            message.error(t('pickup.appointment.messages.pleaseSelectPickupDateFirst'));
            return;
        }
        if (persistentSelection.length < 2) {
            message.warning(t('pickup.appointment.messages.pleaseSelectAtLeastTwoAppointmentsToMerge'));
            return;
        }

        // 校验选中的预约单是否属于同一个客户 (channelId相同)
        const selectedAppointmentsArray = Array.from(selectedAppointments.values());
        const firstChannelId = selectedAppointmentsArray[0].channelId;
        const isSameChannel = selectedAppointmentsArray.every(appointment =>
            appointment.channelId === firstChannelId
        );

        if (!isSameChannel) {
            message.warning('Please select appointments from the same customer.');
            return;
        }
        
        setMergeAppointmentModalVisible(true);
    };

    // 处理合并预约成功
    const handleMergeSuccess = () => {
        // 刷新列表
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();

        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());

        // 关闭模态框
        setMergeAppointmentModalVisible(false);
    };

    // 处理复制预约
    const handleCopyAppointment = () => {
        setCopyAppointmentModalVisible(true);
    };

    // 处理复制预约成功
    const handleCopySuccess = () => {
        // 刷新列表
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();

        // 关闭模态框
        setCopyAppointmentModalVisible(false);
    };

    // 处理数据报表下载
    const handleReportDownload = async () => {
        if (!currentWarehouse?.id) {
            message.error(t('pickup.appointment.messages.pleaseSelectWarehouse') || 'Please select a warehouse first');
            return;
        }

        const fileName = await generateAppointmentReport(
            buildPickupAppointmentDownloadFilters(filters, currentWarehouse.id),
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (fileName) {
            setAppointmentFileName(fileName);
            setAppointmentDownloadVisible(true);
        }
    };

// 处理排序选择变化
    const handleSortChange = (value: string | null) => {
        setSortType(value);
    };

    // 排序选项配置
    const sortOptions = [
        { label: 'Updatetime: Newest First', value: 'Updatetime-asc' },
        { label: 'Updatetime: Oldest First', value: 'Updatetime-desc' },
        { label: 'Address: A → Z', value: 'address-asc' },
        { label: 'Address: Z → A', value: 'address-desc' },
        { label: 'Zipcode: Low → High', value: 'zipcode-asc' },
        { label: 'Zipcode: High → Low', value: 'zipcode-desc' },
    ];
    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">

                {/* 筛选条件 */}
                <PickupDataProvider>
                    <PickupAppointmentFilter
                        filters={filters}
                        setFilters={setFilters}
                        handleFilter={handleFilter}
                        handleResetFilters={handleResetFilters}
                        tempFilters={tempFilters}
                        setTempFilters={setTempFilters}
                        onMapRouting={handleMapRouting}
                        onReportDownload={handleReportDownload}
                        disableMapRouting={!canOperatePickupAppointment}
                    />
                </PickupDataProvider>

                {/* 批量操作区域 */}
                <div className="mt-3 flex justify-between items-center mb-3 pl-0">
                    {/* 左侧 */}
                    <div className="flex items-center gap-2">
                        {t('pickup.appointment.selectedQuantityOfGoods')}: {selectedSummary}
                    </div>

                    {/* 右侧 */}
                    <div className="flex items-center gap-2">
                        <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                            <span className="font-bold">{t('pickup.appointment.buttons.filter')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleReportDownload}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('components.button.download')}</span>
                        </Button>
                        <Button onClick={handleResetFilters} className="w-auto min-w-24">
                            <span className="font-bold">{t('pickup.appointment.buttons.reset')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleConfirmAppointment}
                            loading={confirmLoading}
                            disabled={isPastDate || filters.confirm !== 0 || persistentSelection.length === 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.confirmAppointment')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleCreateAppointment}
                            disabled={isPastDate}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.createAppointment')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleAddTask}
                            disabled={isPastDate || filters.confirm === 0 || persistentSelection.length === 0 || validationLoading }
                            hidden={!canOperatePickupAppointment}
                            loading={validationLoading}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.addTask')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleCreateTask}
                            disabled={isPastDate || filters.confirm === 0 || persistentSelection.length === 0 || validationLoading }
                            loading={validationLoading}
                            hidden={!canOperatePickupAppointment}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.createTask')}</span>
                        </Button>
                        <Button
                            type="primary"
                            hidden={true}
                            onClick={handleMergeAppointment}
                            /*这里可以merge的条件是必须大于2条，并且全是CREATED状态才可以*/
                            disabled={isPastDate || persistentSelection.length < 2 ||
                                (persistentSelection.length >= 2 && Array.from(selectedAppointments.values()).some(appointment => appointment.status !== 'CREATED'))}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.mergeAppointment')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleCopyAppointment}
                            disabled={isPastDate}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.appointment.buttons.copyAppointment')}</span>
                        </Button>

                    </div>
                </div>


                {/* Tab 切换：待确认 / 已确认 */}
                <div className="mb-4">
                    <Tabs
                        activeKey={(filters.confirm === 1 ? 'confirmed' : 'pending')}
                        onChange={handleTabSwitch}
                        tabBarExtraContent={
                            <div className="flex justify-end items-center gap-4">
                                {/* Sort Select */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">{t('pickup.appointment.buttons.sort')}:</span>
                                    <Select
                                        value={sortType}
                                        onChange={handleSortChange}
                                        options={sortOptions}
                                        style={{ width: 200 }}
                                        placeholder={t('pickup.appointment.placeholders.selectSortingOption') || undefined}
                                        allowClear
                                    />
                                </div>
                            </div>
                        }
                        items={[
                            { 
                                key: 'pending', 
                                label: `${t('pickup.appointment.appointmentPageDetails.titles.endingConfirmationAppointment')}${countsLoading ? '' : ` (${pendingCount})`}`
                            },
                            { 
                                key: 'confirmed', 
                                label: `${t('pickup.appointment.appointmentPageDetails.titles.confirmedAppointment')}${countsLoading ? '' : ` (${confirmedCount})`}`
                            }
                        ]}
                        className="[&_.ant-tabs-tab]:font-bold"
                    />
                </div>

                {/* 预约单列表 */}
                <div className="mb-4">
                    <PickupAppointmentColumns
                        appointmentData={sortedAppointmentData}
                        showAppointmentDetail={showAppointmentDetail}
                        showTaskDetail={handleShowTaskDetail}
                        onSelectChange={handleSelectChange}
                        selectedRowKeys={selectedRowKeys}
                        loading={loading}
                        onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                        confirm={filters.confirm}
                    />
                </div>

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* 预约单详情抽屉组件 */}
                <PickupAppointmentDrawer
                    visible={detailVisible}
                    onClose={() => { setDetailVisible(false); setCreateInitialSiteId(undefined); setCreateInitialChannelId(undefined); }}
                    appointmentId={appointmentId}
                    mode={drawerMode}
                    onSuccess={handleDrawerSuccess}
                    listPickupDate={filters.expectedPickupDate}
                    canCreateAppointment={canCreateAppointment}
                    initialCooperationWarehouseId={createInitialSiteId}
                    initialChannelId={createInitialChannelId}
                    initialRepickupSourceId={createInitialRepickupSourceId}
                    initialManualReason={createInitialManualReason}
                />

                {/* 转移任务模态框 */}
                <PickupTransferTaskModal
                    visible={transferTaskModalVisible}
                    onClose={() => setTransferTaskModalVisible(false)}
                    onConfirm={handleTransferConfirm}
                    currentTaskId={currentTaskId}
                    warehouseId={warehouseId ? Number(warehouseId) : 0}
                    taskDate={filters.expectedPickupDate || dayjs().format('YYYY-MM-DD')}
                />

                {/* 创建任务抽屉 */}
                <PickupTaskCreateDrawer
                    key={`create-drawer-${warehouseId}`}
                    visible={taskCreateDrawerVisible}
                    onClose={() => setTaskCreateDrawerVisible(false)}
                    onSuccess={handleTaskCreateSuccess}
                    selectedAppointmentIds={persistentSelection.map((key: React.Key) => Number(key))}
                    pickupDate={getCurrentPickupDate()}
                    taskSource={PickupSourceEnum.APPOINTMENT_LIST}
                />

                {/* 任务详情抽屉 */}
                {taskDetailDrawerVisible && (
                    <PickupDataProvider>
                        <PickupTaskDetailDrawer
                            visible={taskDetailDrawerVisible}
                            onClose={() => setTaskDetailDrawerVisible(false)}
                            taskId={currentTaskId}
                            onSave={handleTaskDetailSuccess}
                        />
                    </PickupDataProvider>
                )}

                {/* 合并预约模态框 */}
                <PickupMergeAppointmentModal
                    visible={mergeAppointmentModalVisible}
                    onClose={() => setMergeAppointmentModalVisible(false)}
                    onSuccess={handleMergeSuccess}
                    selectedAppointments={Array.from(selectedAppointments.values())}
                    pickupDate={filters.expectedPickupDate || dayjs().format('YYYY-MM-DD')}
                />

                {/* 复制预约模态框 */}
                <PickupAppointmentCopyModal
                    visible={copyAppointmentModalVisible}
                    onClose={() => setCopyAppointmentModalVisible(false)}
                    onSuccess={handleCopySuccess}
                    listPickupDate={filters.expectedPickupDate}
                />

                <AppointmentDownloadModal
                    visible={appointmentDownloadVisible}
                    onClose={() => setAppointmentDownloadVisible(false)}
                    fileName={appointmentFileName}
                />
            </div>
    );
};

export default PickupAppointmentsPage;
